import { combineReducers } from "redux";
import {
	createSelector,
	createSelectorCreator,
	defaultMemoize,
} from "reselect";
import isEqual from "lodash";
import answers, {
	selectAllAnswers,
	selectAnswerById,
	selectAnswersError,
} from "./answersSlice";
import pages, {
	selectAllPages,
	selectPageById,
	selectPagesError,
} from "./pagesSlice";
import form, {
	selectForm,
	selectFormError,
	selectFormState,
	selectFormStatus,
	selectSession,
} from "./formSlice";
import questions, {
	selectAllQuestions,
	selectQuestionsError,
} from "./questionsSlice";
import sections, {
	selectAllSections,
	selectSectionsError,
} from "./sectionsSlice";
import lookups, { selectAllLookups, selectLookupsError } from "./lookupsSlice";
import trees, { selectAllTrees, selectTreesError } from "./treesSlice";
import security, { selectAuthToken } from "./securitySlice";
import rows, { selectAllRows, selectRowsError } from "./rowsSlice";
import formulas, {
	selectAllFormulas,
	selectFormulaById,
	selectFormulasError,
} from "./formulasSlice";
import dependencies, {
	selectAllDependencies,
	selectDependenciesError,
} from "./dependenciesSlice";
import attachments, {
	selectAllAttachments,
	selectAttachmentsError,
} from "./attachmentsSlice";
import workflows, {
	selectAllWorkflows,
	selectWorkflowsError,
	selectWorkflowStatus,
} from "./workflowsSlice";
import workflowHistory from "./workflowHistorySlice";
import { validator } from "../utils/common";
import global, { selectGlobal } from "./global";
import dayjs from "dayjs";
import { errorReducer } from "./error";

/* cme specific */
import cme from "../features/cme/reducers";

/* capex specific */
import capex from "../features/capex/reducers";
import { selectAssetRc } from "features/capex/assetRc/assetRcSlice";
import { selectAssetCategory } from "features/capex/assetCategory/assetCategorySlice";
import { selectAllKeyMembers } from "../features/capex/keyMembers/keyMembersSlice";

/* roc specific */
import roc from "../features/roc/reducers";

export default combineReducers({
	answers,
	pages,
	questions,
	sections,
	lookups,
	trees,
	form,
	security,
	rows,
	formulas,
	dependencies,
	attachments,
	workflows,
	workflowHistory,
	cme,
	capex,
	roc,
	errorReducer,
	global,
});

//this ensures our selectors use deep equal for selectors that always return new refs
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const selectCurrentPageId = createSelector(
	[selectSession, selectAllPages],
	(session, pages) => {
		return session.currentPage
			? session.currentPage
			: pages.length > 0
			? pages[0].id
			: null;
	}
);

export const selectLastPageId = (state) => {
	let pagesCount = state.pages?.ids.length || 0;
	if (pagesCount > 0) return state.pages?.ids[pagesCount - 1];
};

export const selectSecurityStatus = (state) => state.security.status;

export const selectSecurityError = (state) => state.security.error;

export const selectAllErrors = createSelector(
	[
		selectFormError,
		selectWorkflowsError,
		selectAnswersError,
		selectDependenciesError,
		selectAttachmentsError,
		selectFormulasError,
		selectLookupsError,
		selectPagesError,
		selectSectionsError,
		selectQuestionsError,
		selectRowsError,
		selectSecurityError,
		selectTreesError,
	],
	(...args) => {
		//each slice of state have props status and error
		//this selector returns an array of the error props, if status is 'failed'
		const errorState = [];
		args.forEach((arg) => {
			if (arg) errorState.push(arg);
		});
		return errorState;
	}
);

export const selectFormDefinition = (state) => state.form?.definition;

export const selectIsLoading = createSelector(
	[selectFormStatus, selectWorkflowStatus],
	(...args) => {
		let isLoading = false;
		args.forEach((arg) => {
			if (arg === "loading") isLoading = true;
		});
		return isLoading;
	}
);

export const selectQuestionsByPage = createSelector(
	//only get questions on current page
	[selectCurrentPageId, selectAllQuestions],
	(currentPage, allQuestions) => {
		return allQuestions.filter((q) => q.pageId === currentPage);
	}
);

export const makeSelectQuestionsBySectionId = (sectionId) => {
	return createDeepEqualSelector([selectAllQuestions], (questions) => {
		return questions.filter((q) => q.sectionId === sectionId);
	});
};

export const selectSectionsByPageId = (pageId) => {
	return createDeepEqualSelector([selectAllSections], (allSections) => {
		return allSections.filter((s) => s.pageId === pageId);
	});
};

export const makeSelectChildQuestions = (id) => {
	return createDeepEqualSelector(
		[selectQuestionsByPage],
		(selectQuestionsByPage) => {
			return selectQuestionsByPage.filter(
				(q) => q.parentQuestionId === id
			);
		}
	);
};

export const makeSelectLookupByDomainCode = (domain) => {
	return createSelector([selectAllLookups], (allLookups) => {
		return allLookups.filter((l) => l.domainCode === domain);
	});
};

export const makeSelectTreeByDomainCode = (domain) => {
	return createSelector([selectAllTrees], (trees) => {
		return trees.find((l) => l.domain === domain)?.tree;
	});
};

export const makeSelectRowsByQuestionId = (questionId) => {
	return createSelector([selectAllRows], (rows) =>
		rows.filter((row) => row.questionId === questionId)
	);
};

export const makeSelectAttachmentsByQuestionId = (questionId) => {
	return createSelector([selectAllAttachments], (attachments) =>
		attachments.filter((a) => a.questionId === questionId)
	);
};

export const selectValueByQuestionId = createSelector(
	[selectAnswerById],
	(answer) => {
		return answer ? answer : null;
	}
);

export const makeSelectFormulaByRowId = (rowId) =>
	createSelector([selectFormulaById], (formula) => {
		if (formula) {
			if (rowId && formula.valuesByRow) {
				return rowId ? formula?.valuesByRow[rowId] : formula.value;
			} else return formula.value;
		}
	});

//#region to determine visible questions
export const selectAllSectionsByCurrentPage = createSelector(
	[selectAllSections, selectCurrentPageId],
	(sections, pageId) => {
		return sections.filter((s) => s.pageId === pageId);
	}
);

export const selectAllVisibleSectionsByCurrentPage = createSelector(
	[selectAllSectionsByCurrentPage, selectAllFormulas],
	(sections, formulas) => {
		return sections.filter((s) => {
			const { visible } = s;
			const formulaId =
				typeof visible === "object" && visible !== null
					? visible.formulaId
					: null;
			return formulaId
				? formulas.find((f) => f.id === formulaId)?.value
				: visible
				? visible
				: true;
		});
	}
);

export const selectAllVisibleQuestions = createSelector(
	[selectAllQuestions, selectAllFormulas],
	(questions, formulas) => {
		return questions.filter((q) => {
			const { visible } = q;
			const formulaId =
				typeof visible === "object" && visible !== null
					? visible.formulaId
					: null;
			return formulaId
				? formulas.find((f) => f.id === formulaId)?.value
				: visible
				? visible
				: true;
		});
	}
);

export const selectAllVisibleQuestionsByCurrentPage = createSelector(
	[selectAllVisibleQuestions, selectAllVisibleSectionsByCurrentPage],
	(questions, sections) => {
		return questions.filter(
			(q) => sections.findIndex((s) => s.id === q.sectionId) !== -1
		);
	}
);
//#endregion to determine visible questions

//#region static type answers

export const selectStaticQuestions = createSelector(
	[selectAllQuestions],
	(questions) => questions.filter((q) => q.staticType)
);

export const selectStaticAnswers = createSelector(
	[selectStaticQuestions, selectAllAnswers],
	(qs, as) => as.filter((a) => qs.some((q) => q.id === a.questionId))
);

export const selectStaticAnswerOrganisationHiearchyId = createSelector(
	[selectStaticQuestions, selectAllAnswers, selectGlobal],
	(qs, as, global) =>
		as.find((a) =>
			qs.some(
				(q) => q.id === a.questionId && q.staticType === "approvalRc"
			)
		)?.value || global.orgRootId
);

export const selectApprovalSummary = createSelector(
	[
		selectAllAnswers,
		selectForm,
		selectAllQuestions,
		selectAllLookups,
		selectAssetRc,
		makeSelectRowsByQuestionId("q1"),
		makeSelectRowsByQuestionId("fundingSourceTable"),
		makeSelectRowsByQuestionId("q8"),
		selectAllAttachments,
		selectAssetCategory,
		selectAllKeyMembers,
		selectAllFormulas,
	],
	(
		answers,
		formData,
		questions,
		lookups,
		rcs,
		purchases,
		funding,
		opex,
		attachments,
		assetCategories,
		keyMembers,
		formulas
	) => {
		const getAnswer = (questionId, answerId = null) => {
			const answer = answers.find(
				(a) =>
					a.questionId === questionId &&
					(a.id === answerId || answerId === null)
			);
			const question = questions.find((q) => q.id === questionId);
			if (question?.id === "s6q1") {
				return rcs.find(
					(l) => l.id.toLowerCase() === answer?.value?.toLowerCase()
				);
			} else if (question?.domain) {
				const lookup = lookups.find(
					(l) =>
						l.domainCode === question.domain &&
						l.code === answer?.value
				);
				//in lookup lists where you can do freetext, you won't find a matching lookup item
				return lookup ?? answer;
			} else return answer;
		};
		const getFormula = (formulaId, rowId) => {
			const formula = formulas.find((f) => f.id === formulaId);
			return formula?.valuesByRow ? formula.valuesByRow[rowId] : null;
		};
		let fileList = attachments.map((a) => {
			return {
				uid: a.id,
				questionId: a.questionId,
				name: a.fileName,
				status: "done",
				fileType: a.fileType,
				url: `${process.env.REACT_APP_API_URL}/UploadDocument/GetUploadDocument/${a.id}`,
			};
		});

		//display data
		const projectCode = getAnswer("projectCode");
		const projectShortName = getAnswer("projectShortName");
		const projectLongName = getAnswer("projectDescription");
		const projectOutline = getAnswer("s7q2");
		const riskAddressed = getAnswer("s7q3");
		const alternatives = getAnswer("s7q4");
		const reasonforInvestment = getAnswer("reasonforInvestment");

		const division = getAnswer("s6q1-2");
		const startDate = formData.createOn
			? dayjs(formData.createOn, "YYYY-MM-DD").format("DD/MM/YYYY")
			: dayjs().format("DD/MM/YYYY");
		const endDate = dayjs(getAnswer("s6q2")?.value).format("DD/MM/YYYY");
		const FPIMSetupType = getAnswer("FPIMSetupType");
		const rc = getAnswer("s6q1");
		const projectNumber = getAnswer("s2q2");

		const isNewRequest = getAnswer("newOrSupQ")?.code === "newRequest";

		const projectBudgetArray =
			purchases && purchases.length > 0
				? purchases.map((f) => {
						const major = getAnswer("q3a", f?.q3a)?.value;
						const minor = getAnswer("q3b", f?.q3b)?.value;
						const category = assetCategories.find(
							(ac) => ac.major === major && ac.minor === minor
						);
						const itemDescription = getAnswer("q2", f?.q2)?.value;
						return {
							id: f.id,
							itemDescription,
							major,
							minor,
							currency:
								getAnswer("q4", f?.q4)?.description ?? "NZD",
							quantity: getAnswer("quantity", f?.quantity)?.value,
							unitPrice: getAnswer("unitPrice", f?.unitPrice)
								?.value,
							total: getFormula("formulaId", f?.id),
							wipAccount: category?.wipAccount,
							lifeYears: category?.lifeYears,
						};
				  })
				: [];

		const fundingArray =
			funding && funding.length > 0
				? funding.map((f) => {
						const source = getAnswer(
							"sourceProjectCode",
							f?.sourceProjectCode
						);
						return {
							id: f.id,
							source: source?.description,
							code: source?.code,
							amount: getAnswer(
								"sourceCommitment",
								f?.sourceCommitment
							)?.value,
							wtProjectCode: getAnswer(
								"sourceWtProjectCode",
								f?.sourceWtProjectCode
							)?.value,
						};
				  })
				: [];

		const opexArray =
			opex && opex.length > 0
				? opex.map((f) => {
						return {
							id: f.id,
							costType:
								getAnswer("costType", f?.costType)
									?.description ??
								getAnswer("costType", f?.costType)?.value,
							years: getAnswer("years", f?.years)?.value,
							amountPerYear: getAnswer(
								"amountPerYear",
								f?.amountPerYear
							)?.value,
							totalOngoingCost: getFormula("ongoingTotal", f?.id),
						};
				  })
				: [];

		const contactPerson = getAnswer("contactPerson");
		const deliveryAddress = getAnswer("deliveryAddress");

		return {
			projectCode: projectCode?.value,
			projectShortName: projectShortName?.value,
			projectLongName: projectLongName?.value,
			projectNumber: projectNumber?.value,
			division: division?.description,
			rc: `${rc?.code} ${rc?.name}`,
			startDate,
			endDate,
			FPIMSetupType: FPIMSetupType?.description,
			projectBudget: projectBudgetArray,
			fundedFrom: fundingArray,
			fileList,
			keyMembers,
			isNewRequest,
			projectOutline: projectOutline?.value,
			riskAddressed: riskAddressed?.value,
			alternatives: alternatives?.value,
			reasonforInvestment: reasonforInvestment?.value,
			contactPerson: contactPerson?.value,
			deliveryAddress: deliveryAddress?.value,
			opex: opexArray,
		};
	}
);

export const selectFormToPost = createSelector(
	[
		selectForm,
		selectFormDefinition,
		selectAllAnswers,
		selectAllFormulas,
		selectAllRows,
		selectAllAttachments,
		selectStaticAnswerOrganisationHiearchyId,
		selectAllKeyMembers,
		selectFormState,
		selectApprovalSummary,
	],
	(
		form,
		definition,
		answers,
		formulas,
		rows,
		attachments,
		organisationHierarchyId,
		keyMembers,
		formState,
		approvalSummary
	) => {
		return {
			id: form.id,
			formId: definition.id,
			appHierarchyId: definition.appHierarchyId,
			organisationHierarchyId,
			content: {
				answers,
				rows,
				formulas,
				attachments,
				keyMembers,
				approvalSummary,
			},
			status: form.status,
			state: {
				...formState,
			},
		};
	}
);

export const selectStaticAnswerAssetDivision = createSelector(
	[selectStaticQuestions, selectAllAnswers],
	(qs, as) =>
		as.find((a) =>
			qs.some(
				(q) => q.id === a.questionId && q.staticType === "assetDivision"
			)
		)?.value
);

//#endregion static type answers

//#region hacks for Capex

export const makeSelectSumByFormulaId = (formulaId) =>
	createSelector([selectAllFormulas], (formulas) => {
		const formula = formulas.find((f) => f.id === formulaId);
		if (formula?.valuesByRow) {
			return Object.keys(formula.valuesByRow).reduce((acc, val) => {
				return (acc += formula.valuesByRow[val]);
			}, 0);
		} else return 0;
	});

export const selectTotalFundingSource = createSelector(
	[selectAllAnswers],
	(answers) => {
		const fsA = answers.filter(
			(a) =>
				a.questionId === "sourceCommitment" ||
				a.questionId === "externalSourceCommitment"
		);
		return fsA.reduce((acc, a) => {
			return a.value ? acc + a.value : acc;
		}, 0);
	}
);

export const selectTotalNbv = createSelector(
	[selectAllQuestions, selectAllAnswers],
	(questions, answers) => {
		const fsQ = questions.filter((q) => q.id === "NBV");
		const fsA = answers.filter((a) =>
			fsQ.some((q) => q.id === a.questionId)
		);
		return fsA.reduce((acc, a) => {
			return a.value ? acc + a.value : acc;
		}, 0);
	}
);

export const selectTotalFundingDifference = createSelector(
	[makeSelectSumByFormulaId("formulaId"), selectTotalFundingSource],
	(cost, funding) => {
		return funding - cost;
	}
);

export const selectServiceImpact = createSelector(
	[selectAllAnswers],
	(answers) => {
		return answers.find((a) => a.questionId === "serviceImpact");
	}
);

export const selectPatientStaffImpact = createSelector(
	[selectAllAnswers],
	(answers) => {
		return answers.find((a) => a.questionId === "patientStaffImpact");
	}
);

export const selectLikelihoodOfAssetFailing = createSelector(
	[selectAllAnswers],
	(answers) => {
		return answers.find((a) => a.questionId === "likelihoodOfAssetFailing");
	}
);

export const selectAssetCriticalityScore = createSelector(
	[selectServiceImpact, selectPatientStaffImpact],
	(serviceImpact, patientStaffImpact) => {
		const x = serviceImpact?.value ? parseInt(serviceImpact.value) : 0;
		const y = patientStaffImpact?.value
			? parseInt(patientStaffImpact.value)
			: 0;
		/*
		calculation logic
		-if either number is 0, then 0 (stands for NO SCORE)
		-if both numbers are same, then the same number is returned
		-if they are different, then it is 
			a) take the bigger number
			b) minus the (difference between the two numbers divided by two, rounded up)
			1 & 2 = 2
			2 & 2 = 2
			3 & 2 = 3
			4 & 2 = 3			
			5 & 2 = 4
		*/
		return x === 0 || y === 0
			? 0
			: x === y
			? x
			: x > y
			? x - parseInt((x - y) / 2)
			: y - parseInt((y - x) / 2);
	}
);
export const selectLikelihoodScore = createSelector(
	[selectLikelihoodOfAssetFailing],
	(likelihood) => {
		return likelihood?.value ? parseInt(likelihood.value) : 0;
	}
);

export const selectOverallRiskScore = createSelector(
	[selectAssetCriticalityScore, selectLikelihoodScore],
	(x, y) => {
		return x === 0 || y === 0 ? 0 : x * y;
	}
);

const selectWeighting1 = createSelector([selectAllAnswers], (answers) => {
	return answers.find((a) => a.questionId === "weighting1")?.value;
});
const selectWeighting2 = createSelector([selectAllAnswers], (answers) => {
	return answers.find((a) => a.questionId === "weighting2")?.value;
});
const selectWeighting3 = createSelector([selectAllAnswers], (answers) => {
	return answers.find((a) => a.questionId === "weighting3")?.value;
});
const selectLikelihoodConsequences = createSelector(
	[selectAllAnswers],
	(answers) => {
		return answers.find((a) => a.questionId === "likelihoodOfConsequence")
			?.value;
	}
);

export const selectOverallRiskConsequences = createSelector(
	[
		selectWeighting1,
		selectWeighting2,
		selectWeighting3,
		selectLikelihoodConsequences,
	],
	(con1, con2, con3, likelihood) => {
		const total =
			(con1 ? parseInt(con1) : 0) +
			(con2 ? parseInt(con2) : 0) +
			(con3 ? parseInt(con3) : 0);

		const count = (con1 ? 1 : 0) + (con2 ? 1 : 0) + (con3 ? 1 : 0);

		const x = Math.round(total / count, 0);
		const y = likelihood ? parseInt(likelihood) : 0;

		return x === 0 || y === 0 ? 0 : x * y;
	}
);

//#endregion hacks for Capex

export const selectAllInvalidQuestionsByCurrentPage = createSelector(
	[
		selectAllVisibleQuestionsByCurrentPage,
		selectAllAnswers,
		selectAllRows,
		selectTotalFundingDifference,
		selectAllKeyMembers,
		selectCurrentPageId,
	],
	(
		questions,
		answers,
		rows,
		totalFundingDifference,
		keyMembers,
		currentPageId
	) => {
		let allQuestions = [];

		//create an array of all quetions including child questions in rows
		questions.forEach((q) => {
			const { parentQuestionId } = q;
			if (parentQuestionId) {
				allQuestions.push(
					...rows
						.filter((r) => r.questionId === parentQuestionId)
						.map((r) => {
							return { ...q, id: r[q.id] };
						})
				);
			} else {
				allQuestions.push(q);
			}
		});

		//test for missing key members
		const missingMandatoryKeyMembers = keyMembers.filter(
			({ mandatory, fullName }) => mandatory && !fullName
		);

		//push missing key member error if on the key member page
		if (
			missingMandatoryKeyMembers.length > 0 &&
			currentPageId === "keyMembers"
		) {
			allQuestions.push({
				id: "keyMembers",
				errors: missingMandatoryKeyMembers.length,
			});
		}

		//detect errors for each question
		const errorQuestions = allQuestions.map((q) => {
			const { id, validation } = q;
			let errors = 0;
			if (validation && validation.length > 0) {
				// eslint-disable-next-line array-callback-return
				errors = validation.reduce((acc, val) => {
					const answerObject =
						id === "totalFundingDifference"
							? totalFundingDifference
							: answers.find((a) => a.id === q.id);
					const answer =
						id === "totalFundingDifference"
							? answerObject
							: answerObject?.value === undefined
							? null
							: answerObject.value;
					let result = validator(val, answer);
					if (result) return acc + 1;
					else return acc;
				}, 0);
			}
			if (q.errors)
				return q; //if the question already has errors (i.e. keyMembers) return it
			else return { ...q, errors };
		});

		//consolidate questions up to the parent level by summing children errors
		const parentLevelErrorQuestions = errorQuestions
			.filter((q) => !q.parentQuestionId)
			.map((q) => {
				if (q.inputType !== "Table") return q;
				else {
					//for table questions, sum all children errors as there may be multiple instances
					const childrenErrors = errorQuestions.reduce((acc, cq) => {
						const { parentQuestionId, errors } = cq;
						return parentQuestionId === q.id ? acc + errors : acc;
					}, 0);
					return { ...q, errors: childrenErrors };
				}
			});
		return parentLevelErrorQuestions.filter((pq) => pq.errors > 0);
	}
);

export const selectIsPageValid = createSelector(
	[selectAllInvalidQuestionsByCurrentPage],
	(questions) => {
		return questions.length === 0;
	}
);

export {
	selectPageById,
	selectAllQuestions,
	selectAllAnswers,
	selectAllLookups,
	selectAllPages,
	selectAllSections,
	selectAllTrees,
	selectAllAttachments,
	selectAllFormulas,
	selectAllRows,
	selectForm,
	selectAllDependencies,
	selectAllWorkflows,
	selectSession,
	selectAuthToken,
};
